import React from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@fluentui/react-components";
import { CalendarClock20Regular } from "@fluentui/react-icons/svg/calendar-clock";
import { CalendarError20Regular } from "@fluentui/react-icons/svg/calendar-error";
import { CheckboxIndeterminate20Regular } from "@fluentui/react-icons/svg/checkbox-indeterminate";
import { CheckmarkCircle20Regular } from "@fluentui/react-icons/svg/checkmark-circle";
import { ClipboardTaskListLtrRegular } from "@fluentui/react-icons/svg/clipboard-task-list-ltr";
import { Clock20Regular } from "@fluentui/react-icons/svg/clock";
import { Warning20Regular } from "@fluentui/react-icons/svg/warning";
import type { MetricKey } from "@onetasklist/shared/viewmodels/board/board-overview/boardOverviewMetrics.types";
import { useBoardOverviewText } from "../board-overview/BoardOverview.i18n";
import SidebarSection from "./SidebarSection";
import { useSidebarOverviewFiltersViewModel } from "./SidebarOverviewFilters.ViewModel";
import "./SidebarOverviewFilters.scss";

interface SidebarOverviewFiltersProps {
  isCollapsed: boolean;
}

const ICONS: Record<MetricKey, React.ReactElement> = {
  activeTasks: <CheckboxIndeterminate20Regular />,
  openSubTasks: <ClipboardTaskListLtrRegular />,
  overdueTasks: <CalendarError20Regular />,
  atRiskTasks: <Warning20Regular />,
  oldTasks: <Clock20Regular />,
  avgAge: <CalendarClock20Regular />,
  doneTasks: <CheckmarkCircle20Regular />,
};

/**
 * The board's overview numbers, as filters in the pane.
 *
 * They used to be a row of tiles above the board, which read as a summary and
 * contradicted the board whenever a filter was on. As rows here they read as
 * what they always were: ways of narrowing to the work behind each number.
 */
const SidebarOverviewFilters: React.FC<SidebarOverviewFiltersProps> = ({
  isCollapsed,
}) => {
  const { t } = useTranslation();
  const text = useBoardOverviewText(t);
  const vm = useSidebarOverviewFiltersViewModel();

  return (
    // The section renders a fragment, so the tour needs this box to point at.
    <div className="sidebar-overview" data-tour="sidebar-metrics">
      <SidebarSection
        title={t("board.filters.overviewHeader")}
        isOpen={vm.isSectionOpen}
        onToggle={vm.toggleSection}
        isPaneCollapsed={isCollapsed}
      >
        {vm.filters.map(({ key, value, isSelected, isEmpty }) => (
          <Tooltip
            key={key}
            // Collapsed, the row is only an icon and a number, so the tip has to
            // name the metric before it can explain it.
            content={
              isCollapsed ? (
                <>
                  <strong>{text.getMetricLabel(key)}</strong>
                  <div>{text.getMetricDescription(key)}</div>
                </>
              ) : (
                text.getMetricDescription(key)
              )
            }
            relationship="description"
            positioning="after"
          >
            <button
              type="button"
              aria-label={text.getMetricAriaLabel(key, value)}
              className={`sidebar-nav__link sidebar-nav__link--compact sidebar-nav__link--filter${isSelected ? " sidebar-nav__link--selected" : ""}`}
              aria-pressed={isSelected}
              // Nothing to narrow to, so the row is inert rather than hidden —
              // a zero is information, and the list staying put matters more.
              disabled={isEmpty}
              onClick={() => vm.toggleFilter(key)}
            >
              <span className="sidebar-overview__icon">{ICONS[key]}</span>
              {!isCollapsed && (
                <span className="sidebar-nav__label">
                  {text.getMetricLabel(key)}
                </span>
              )}
              <span
                className={`sidebar-nav__badge${isEmpty ? " sidebar-nav__badge--zero" : ""}`}
              >
                {value}
              </span>
            </button>
          </Tooltip>
        ))}
      </SidebarSection>
    </div>
  );
};

export default SidebarOverviewFilters;
